import { useEffect, useState } from 'react';
import { TRACKS } from '../../constants/appConstants.js';
import {
  adminListLearners, adminCohortMemory, adminDecisionTraces, adminLatestMoods, adminProgressRows,
  deriveOverview, deriveRevenue, weeklyBuckets, countBy, completionRate,
} from '../../services/adminClient.js';
import { T, card, eyebrow, sectionTitle, PageHeader, StatCard, LineChart, BarRow, subscriptionBadge } from './ui.jsx';

const trackName = (id) => TRACKS[id]?.label || id || '—';

export default function AdminOverview() {
  const [learners, setLearners] = useState(null);
  const [cohort, setCohort] = useState([]);
  const [traces, setTraces] = useState([]);
  const [moods, setMoods] = useState({});
  const [progress, setProgress] = useState([]);

  useEffect(() => {
    let c = false;
    Promise.all([adminListLearners(), adminCohortMemory(), adminDecisionTraces(56), adminLatestMoods(), adminProgressRows()])
      .then(([l, m, t, md, pr]) => { if (c) return; setLearners(l || []); setCohort(m || []); setTraces(t || []); setMoods(md || {}); setProgress(pr || []); })
      .catch(() => { if (!c) setLearners([]); });
    return () => { c = true; };
  }, []);

  if (!learners) return <p style={{ color: T.slate, fontSize: 13 }}>Loading dashboard…</p>;

  const ov  = deriveOverview(learners);
  const rev = deriveRevenue(learners);
  const activity = weeklyBuckets(traces, 'created_at', 8);
  const tracks = countBy(learners.map(l => ({ t: trackName(l.active_track_id || l.current_track) })), 't');
  const activeBuckets = cohort.filter(c => c.sample_size >= 8).length;
  const topBucket = cohort.find(c => c.common_misconception) || cohort[0] || null;
  const lowMood = Object.values(moods).filter(m => m.mood === 'stressed' || m.mood === 'tired').length;
  const recent = learners.slice(0, 6);

  const alerts = [];
  if (cohort.length === 0) alerts.push({ c: T.amber, t: 'Cohort memory is empty — M[Emory] insights stay gated until learners generate concept data.' });
  if (lowMood > 0) alerts.push({ c: T.coral, t: `${lowMood} learner${lowMood > 1 ? 's' : ''} reported low energy (stressed/tired) recently.` });
  if (ov.paid === 0) alerts.push({ c: T.slate, t: 'No paid conversions yet — discovery sprint in progress.' });
  if (alerts.length === 0) alerts.push({ c: T.emerald, t: 'All clear — no priority alerts.' });

  const tasks = [
    'Review new sign-ups and onboarding completion',
    'Check the quiz-fallback log in Support',
    'Confirm Stripe webhook + subscription sync',
    'Scan M[Emory] buckets approaching N≥8',
  ];

  const btn = { padding: '9px 16px', borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', border: 'none' };

  return (
    <div>
      <PageHeader title="Admin Dashboard" subtitle="Learner, curriculum, business, and system health — at a glance."
        right={<div style={{ display: 'flex', gap: 8 }}>
          <button type="button" style={{ ...btn, background: '#fff', border: `1px solid ${T.border}`, color: T.slate }}>Open Tasks</button>
          <button type="button" style={{ ...btn, background: T.amber, color: '#fff' }}>Export Report</button>
        </div>} />

      {/* Stat row */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 16 }}>
        <StatCard label="Total Learners" value={ov.total} delta={`${ov.active7} active in 7d`} deltaColor={T.emerald} tint={T.amber}
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="9" cy="7" r="4"/><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/></svg>} />
        <StatCard label="Lesson Completion" value={completionRate(progress) != null ? `${completionRate(progress)}%` : '—'} delta={completionRate(progress) != null ? 'of touched lessons' : 'pending data'} deltaColor={T.slate} tint={T.emerald}
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M20 6 9 17l-5-5"/></svg>} />
        <StatCard label="Trial → Paid" value={`${ov.trialToPaid}%`} delta={`${ov.paid} of ${ov.total}`} deltaColor={T.slate} tint={T.amber}
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 17 9 11 13 15 21 7"/><polyline points="15 7 21 7 21 13"/></svg>} />
        <StatCard label="System Health" value="Healthy" delta="all services live" deltaColor={T.emerald} tint={T.emerald}
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>} />
      </div>

      {/* Two columns */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* LEFT */}
        <div style={{ flex: '1.7 1 420px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={card}>
            <p style={sectionTitle}>Learner Activity</p>
            <p style={{ fontSize: 12, color: T.slate, margin: '3px 0 12px' }}>Decision traces per week (real learning turns, demo excluded)</p>
            <LineChart points={activity} color={T.emerald} />
          </div>

          {/* M[Emory] insights — dark */}
          <div style={{ background: T.surfaceDark, borderRadius: 14, padding: '18px 20px', color: '#e8e6e1' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: T.emerald, boxShadow: '0 0 0 3px rgba(5,150,105,0.25)' }} />
              <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', color: T.emerald }}>M[Emory] Insights</span>
            </span>
            <p style={{ fontSize: 14, color: '#fff', margin: '11px 0 0', lineHeight: 1.55 }}>
              {topBucket
                ? `Emerging signal: ${topBucket.common_misconception || topBucket.concept_key} ${topBucket.track_id ? `across ${trackName(topBucket.track_id)} learners` : ''}.`
                : 'Cohort memory is building. Concept buckets fill as learners study; insights surface once a bucket reaches N ≥ 8.'}
            </p>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', margin: '8px 0 0' }}>
              {activeBuckets} active bucket{activeBuckets === 1 ? '' : 's'} (N ≥ 8) · {cohort.length} total
            </p>
          </div>

          {/* Recent learners */}
          <div style={card}>
            <p style={{ ...sectionTitle, marginBottom: 12 }}>Recent Learners</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {recent.map(l => (
                <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: `1px solid ${T.border}` }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 13, fontWeight: 600, color: T.ink, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.email}</p>
                    <p style={{ fontSize: 11, color: T.slate, margin: '1px 0 0' }}>{trackName(l.active_track_id || l.current_track)}</p>
                  </div>
                  {subscriptionBadge(l.subscription_status, l.tier)}
                  <span style={{ fontSize: 11, color: T.slate, flexShrink: 0, width: 78, textAlign: 'right' }}>{l.created_at ? new Date(l.created_at).toLocaleDateString() : ''}</span>
                </div>
              ))}
              {recent.length === 0 && <p style={{ fontSize: 13, color: T.slate, margin: 0 }}>No learners yet.</p>}
            </div>
          </div>
        </div>

        {/* RIGHT */}
        <div style={{ flex: '1 1 300px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={card}>
            <p style={{ ...sectionTitle, marginBottom: 12 }}>Priority Alerts</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
              {alerts.map((a, i) => (
                <div key={i} style={{ display: 'flex', gap: 9, alignItems: 'flex-start' }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: a.c, marginTop: 5, flexShrink: 0 }} />
                  <span style={{ fontSize: 12.5, color: T.ink, lineHeight: 1.45 }}>{a.t}</span>
                </div>
              ))}
            </div>
          </div>

          <div style={card}>
            <p style={{ ...sectionTitle, marginBottom: 12 }}>Today's Tasks</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
              {tasks.map((t, i) => (
                <label key={i} style={{ display: 'flex', gap: 9, alignItems: 'flex-start', cursor: 'pointer' }}>
                  <input type="checkbox" style={{ marginTop: 2, width: 15, height: 15, accentColor: '#d97706', flexShrink: 0 }} />
                  <span style={{ fontSize: 12.5, color: T.ink, lineHeight: 1.45 }}>{t}</span>
                </label>
              ))}
            </div>
          </div>

          <div style={card}>
            <p style={{ ...sectionTitle, marginBottom: 12 }}>Track Performance</p>
            <BarRow rows={tracks} color={T.amber} />
            <p style={{ fontSize: 11, color: T.slate, margin: '10px 0 0' }}>Learners per track · completion % needs lesson_progress access.</p>
          </div>

          <div style={card}>
            <p style={{ ...sectionTitle, marginBottom: 4 }}>Revenue Snapshot</p>
            <p style={{ fontSize: 11, color: T.slate, margin: '0 0 12px' }}>Estimated · per-tier split pending Stripe</p>
            {[['Paid users', String(rev.paid)], ['Est. MRR (blended)', `$${rev.blendedMrr.toFixed(2)}`], ['Infra est.', `$${rev.infra.toFixed(2)}`], ['Est. margin', `$${rev.margin.toFixed(2)}`]].map(([k, v], i, arr) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: i < arr.length - 1 ? `1px solid ${T.border}` : 'none' }}>
                <span style={{ fontSize: 12.5, color: T.slate }}>{k}</span>
                <span style={{ fontSize: 12.5, fontWeight: 800, color: k.includes('margin') ? T.emerald : T.ink }}>{v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
